import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { listWorkspaces, createWorkspace, moveTrackToWorkspace, Track, Workspace, fetchWorkspaces, loadDb } from "../../data/schema";
import { showToast } from "components/ui-kit/Toast";
import { generateOrganicPattern } from "../../../../utils/patternGenerator";

type Props = {
  open: boolean;
  track: Track | null;
  selectedTrackIds?: Set<string>; // Optional: IDs of selected tracks for bulk operations
  onClose: () => void;
  onMoved?: () => void;
};

export const MoveToWorkspaceModal: React.FC<Props> = ({ open, track, selectedTrackIds, onClose, onMoved }) => {
  const [workspaces, setWorkspaces] = useState(listWorkspaces());
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const max = 100;

  // Вычисляем воркспейсы, в которых уже лежит выбранный трек(и),
  // чтобы не предлагать их как цель перемещения.
  const excludedWorkspaceIds = useMemo(() => {
    const ids = new Set<string>();
    if (!track) return ids;

    const db = loadDb();

    const trackIdsToCheck =
      selectedTrackIds && selectedTrackIds.size > 0
        ? Array.from(selectedTrackIds)
        : [track.id];

    const allWorkspaces = Object.values(db.workspaces) as Workspace[];
    allWorkspaces.forEach(ws => {
      if (trackIdsToCheck.some(id => ws.trackIds.includes(id))) {
        ids.add(ws.id);
      }
    });

    return ids;
  }, [track, selectedTrackIds, refreshKey]);

  useEffect(() => {
    let isMounted = true;
    const sync = async () => {
      if (!open) return;
      setWorkspaces(listWorkspaces());
      setNewWorkspaceName("");
      try {
        const fresh = await fetchWorkspaces();
        if (isMounted) {
          setWorkspaces(fresh);
        }
      } catch (error) {
        console.error("Unable to fetch workspaces", error);
      }
    };
    sync();
    return () => {
      isMounted = false;
    };
  }, [open, refreshKey]);

  const handleWorkspaceClick = async (workspaceId: string) => {
    if (!track) return;
    
    const workspace = workspaces.find(w => w.id === workspaceId);
    const workspaceName = workspace?.name || "Workspace";
    
    // Get track IDs to move (either from selectedTrackIds or just the single track)
    const trackIdsToMove = selectedTrackIds && selectedTrackIds.size > 0
      ? Array.from(selectedTrackIds)
      : [track.id];
    
    const db = loadDb();
    const tracksToMove = trackIdsToMove
      .map(id => db.tracks[id])
      .filter((t): t is Track => Boolean(t));
    
    let movedCount = 0;
    let errorCount = 0;
    
    // Закрываем модалку сразу после выбора воркспейса
    onClose();

    // Process tracks sequentially (дальше продолжаем в фоне)
    for (const t of tracksToMove) {
      try {
        const success = await moveTrackToWorkspace(t.id, workspaceId);
        if (success) {
          movedCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
        console.error(`Failed to move track ${t.id}:`, error);
      }
    }
    
    if (movedCount > 0) {
      showToast(
        movedCount === 1
          ? `Track moved to ${workspaceName}`
          : `${movedCount} tracks moved to ${workspaceName}`,
        undefined,
        'success'
      );
    }
    if (errorCount > 0) {
      showToast(
        `${errorCount} track(s) failed to move`,
        undefined,
        'error'
      );
    }
    
    if (movedCount > 0) {
      setRefreshKey(k => k + 1);
      onMoved?.();
    }
  };

  const handleCreateWorkspace = async () => {
    if (!track || isCreating) return;
    
    setIsCreating(true);
    try {
      const workspace = await createWorkspace(newWorkspaceName.slice(0, max));
    
      // Get track IDs to move (either from selectedTrackIds or just the single track)
      const trackIdsToMove =
        selectedTrackIds && selectedTrackIds.size > 0 ? Array.from(selectedTrackIds) : [track.id];
      
      const db = loadDb();
      const tracksToMove = trackIdsToMove
        .map(id => db.tracks[id])
        .filter((t): t is Track => Boolean(t));
      
      let movedCount = 0;
      let errorCount = 0;
      
      // Process tracks sequentially
      for (const t of tracksToMove) {
        try {
          const success = await moveTrackToWorkspace(t.id, workspace.id);
          if (success) {
            movedCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
          console.error(`Failed to move track ${t.id}:`, error);
        }
      }
      
      if (movedCount > 0) {
        showToast(
          movedCount === 1
            ? `Track moved to ${workspace.name}`
            : `${movedCount} tracks moved to ${workspace.name}`,
          undefined,
          'success'
        );
      }
      if (errorCount > 0) {
        showToast(
          `${errorCount} track(s) failed to move`,
          undefined,
          'error'
        );
      }
      
      setNewWorkspaceName("");
      setRefreshKey(k => k + 1);
      onMoved?.();
      if (movedCount > 0) {
        onClose();
      }
    } catch (error) {
      console.error("Failed to create workspace", error);
      showToast("Unable to create workspace. Please try again.", undefined, "error");
    } finally {
      setIsCreating(false);
    }
  };

  const remaining = useMemo(() => `${newWorkspaceName.length}/${max}`, [newWorkspaceName, max]);

  if (!open) return null;

  const content = (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Move to Workspace</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        
        <WorkspaceList>
          {workspaces
            .filter((workspace: Workspace) => !excludedWorkspaceIds.has(workspace.id))
            .map((workspace: Workspace) => {
            const trackCount = workspace.trackIds.length;
            return (
              <WorkspaceItem key={workspace.id} onClick={() => handleWorkspaceClick(workspace.id)}>
                <WorkspaceCover>
                  <CoverPattern $patternUrl={generateOrganicPattern(workspace.id)} />
                </WorkspaceCover>
                <WorkspaceContent>
                  <WorkspaceTitle>{workspace.name}</WorkspaceTitle>
                  <WorkspaceMeta>
                    {trackCount} {trackCount === 1 ? 'Song' : 'Songs'}, {workspace.updatedAt ? new Date(workspace.updatedAt).toLocaleString() : workspace.createdAt ? new Date(workspace.createdAt).toLocaleString() : 'Recently'}
                  </WorkspaceMeta>
                </WorkspaceContent>
              </WorkspaceItem>
            );
          })}
        </WorkspaceList>

        <CreateSection>
          <InputRow>
            <CreateInput
              placeholder="Untitled Workspace"
              value={newWorkspaceName}
              maxLength={max}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateWorkspace();
                }
              }}
            />
            <CharCounter>{remaining}</CharCounter>
          </InputRow>
          <CreateButton onClick={handleCreateWorkspace} disabled={isCreating}>
            {isCreating ? "Creating..." : "Create Workspace"}
          </CreateButton>
        </CreateSection>
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }
  return createPortal(content, document.body);
};


const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 44vw;
  max-width: calc(100% - 6vw);
  background: #fff;
  color: #000;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 2.2vh 2vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    margin: 0 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #000;
`;

const CloseButton = styled.button`
  width: 3.4vh;
  height: 3.4vh;
  border-radius: 1.7vh;
  border: none;
  background: #f4f4f4;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkspaceList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 20px;
  max-height: calc(5 * 80px); /* 5 items max, each ~80px tall */
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f4f4f4;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
    
    &:hover {
      background: #999;
    }
  }
`;

const WorkspaceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6vh;
  padding: 1.4vh;
  margin: 10px 10px;
  border-radius: 12px;
  background: #fff;
  box-shadow: none;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;

  &:hover {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const WorkspaceCover = styled.div`
  position: relative;
  width: 12vh;
  height: 9vh;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
`;

const CoverPattern = styled.div<{ $patternUrl: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("${p => p.$patternUrl}");
  background-size: cover;
  background-position: center;
`;

const WorkspaceContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const WorkspaceTitle = styled.div`
  font-weight: 600;
  color: #333;
`;

const WorkspaceMeta = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

const CreateSection = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  align-items: flex-end;
`;

const InputRow = styled.div`
  position: relative;
  flex: 1;
`;

const CreateInput = styled.input`
  width: 100%;
  padding: 1.4vh 1.2vw;
  padding-right: 60px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    border-color: #7987FF;
    background: #fff;
  }
`;

const CharCounter = styled.div`
  position: absolute;
  right: 14px;
  bottom: 1vh;
  color: #999;
  font-size: 12px;
  pointer-events: none;
`;

const CreateButton = styled.button`
  padding: 1.2vh 1.4vw;
  border-radius: 18px;
  border: none;
  background: #7987FF;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
  
  &:hover {
    background: #6b7cff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #7987FF;
  }
`;

export default MoveToWorkspaceModal;

